/**
 * CalendarPostPopover Component
 *
 * Centralized popover content for posts displayed ON the calendar (committed/mapped posts).
 * Used by: HorizontalPostCard, SquarePostCard, WeekPostCard, CompactPostCard,
 *          CalendarDayCell, CalendarDayPosts, CalendarDayView, CalendarWeekView
 */

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faFacebook,
  faInstagram,
  faLinkedin,
  faYoutube,
  faTwitter,
  faTiktok,
  faPinterest,
  faThreads,
  faGoogle,
  faWordpress,
} from '@fortawesome/free-brands-svg-icons';
import { faCloud } from '@fortawesome/free-solid-svg-icons';
import { Post } from '@/services/posts';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { useTimezone } from '@/contexts';
import { usePostPermissions } from '@/hooks/usePostPermissions';
import { getStatusInfo, getStatusBadge, isOccurrenceStatus } from '@/utils/calendarStatus';
import { getPlatformDisplayName } from '@/constants/platformLimits';
import { CarouselThumb } from './CarouselThumb';
import { CaptionWithReadMore } from './CaptionWithReadMore';

// Platform icons mapping
const platformIcons: Record<string, IconDefinition> = {
  FACEBOOK: faFacebook,
  INSTAGRAM: faInstagram,
  LINKEDIN: faLinkedin,
  TWITTER: faTwitter,
  YOUTUBE: faYoutube,
  GOOGLE_MY_BUSINESS: faGoogle,
  PINTEREST: faPinterest,
  TIKTOK: faTiktok,
  THREADS: faThreads,
  BLUESKY: faCloud,
  WORDPRESS: faWordpress,
};

/**
 * Per-channel status label key (Task #182 R4-5 #3).
 *
 * `pc.status` is the raw `ChannelInstanceStatus` the backend copies straight
 * from `ScheduledChannelInstance.status` (PENDING/PUBLISHING/PUBLISHED/
 * FAILED/RETRYING). Map every value to its translated `common:` label so the
 * per-channel tooltip never renders a raw enum; `status_unknown` is the
 * defensive fallback for anything outside this set. Mirrors
 * `getChannelStatusLabel` in `pages/BrandCheck.tsx`.
 */
const CHANNEL_STATUS_LABEL_KEYS: Record<string, string> = {
  PENDING: 'common:status_pending',
  PUBLISHING: 'common:status_publishing',
  PUBLISHED: 'common:status_published',
  FAILED: 'common:status_failed',
  RETRYING: 'common:retrying',
};

const getChannelStatusLabel = (status: string, t: (key: string) => string): string =>
  t(CHANNEL_STATUS_LABEL_KEYS[status] ?? 'common:status_unknown');

interface CalendarPostPopoverProps {
  post: Post;
  inferredStatus: string;
  onEdit?: (post: Post) => void;
  onDelete?: (post: Post) => void;
}

export const CalendarPostPopover = ({
  post,
  inferredStatus,
  onEdit,
  onDelete,
}: CalendarPostPopoverProps) => {
  const { t } = useTranslation('common');
  const { formatTimeOnly } = useTimezone();
  const navigate = useNavigate();
  const { canEditPost } = usePostPermissions();

  const isBlog = post.source === 'blog';
  const firstMedia = post.postMedia?.[0];
  const mediaCount = post.postMedia?.length ?? 0;
  // Stage 2c-ii: `inferredStatus` here is always a genuine first-class
  // `OccurrenceStatus` in practice (this popover is only ever fed calendar
  // occurrence entries — see the callers listed above) — layer its
  // label/color/icon (`getStatusBadge`) over the base gradient/
  // headerTextColor from `getStatusInfo` so Partial/Missed/Cancelled/
  // Publishing render as themselves ("Partial success", not a raw/untranslated
  // "status_partial" key). Falls back to the legacy translated label entirely
  // when `inferredStatus` isn't a genuine occurrence value (defensive).
  const statusInfo = isOccurrenceStatus(inferredStatus)
    ? { ...getStatusInfo(inferredStatus, t), ...getStatusBadge(inferredStatus, t) }
    : getStatusInfo(inferredStatus, t);

  // Status ring colors
  const ringColors: Record<string, string> = {
    PUBLISHED: 'ring-emerald-500',
    FAILED: 'ring-red-500',
    PENDING: 'ring-amber-400',
    SCHEDULED: 'ring-blue-500',
    PUBLISHING: 'ring-yellow-500',
  };

  // Mapped items must remain openable for read-only review and scheduling. Only
  // the new queued/scheduled Edit action depends on content edit permission.
  const canEdit = !isBlog && (inferredStatus === 'MAPPED' || (inferredStatus === 'SCHEDULED' && canEditPost(post)));
  const canDelete = !isBlog && (inferredStatus === 'MAPPED' || inferredStatus === 'SCHEDULED');

  // Determine if drag footer should show
  const canDrag = !isBlog && (inferredStatus === 'MAPPED' || inferredStatus === 'SCHEDULED');

  return (
    <div className="flex max-h-full flex-col lg:max-h-[70vh]">
      {/* Header — fixed */}
      <div className={`bg-gradient-to-r ${statusInfo.gradient} flex-shrink-0 px-5 py-3 pr-[72px] lg:px-4 lg:py-3`}>
        <div className="flex min-w-0 flex-wrap items-center justify-start gap-x-2 gap-y-1">
          <Icon name={statusInfo.icon} size="sm" className={statusInfo.headerTextColor} ariaHidden />
          <span className={`${statusInfo.headerTextColor} font-medium text-sm`}>{statusInfo.label}</span>
          {post.scheduledAt && (
            <span className={`${statusInfo.headerTextColor === 'text-white' ? 'text-white/80' : 'text-gray-900/80'} text-xs lg:hidden`}>
              · {formatTimeOnly(post.scheduledAt)}
            </span>
          )}
          {/* Variation badge */}
          {post.variationTitle && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-secondary-500 text-secondary-contrast">
              <Icon name={ICONS.COPY} size="xs" ariaHidden />
              {post.variationTitle}
            </span>
          )}
        </div>
        {post.scheduledAt && (
          <div className={`${statusInfo.headerTextColor === 'text-white' ? 'text-white/80' : 'text-gray-900/80'} mt-1 hidden items-center gap-1 text-xs lg:flex`}>
            <Icon name={ICONS.CLOCK} size="xs" className={statusInfo.headerTextColor === 'text-white' ? 'text-white/80' : 'text-gray-900/80'} ariaHidden />
            {formatTimeOnly(post.scheduledAt)}
          </div>
        )}
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto min-h-0 scrollbar-none">
      {/* Omit the square media placeholder when this post has no media. */}
      {firstMedia && (firstMedia.thumbnailUrl || firstMedia.originalUrl) && (
        <div className="px-5 py-3 lg:p-[9px]">
          <CarouselThumb
            media={post.postMedia || []}
            mediaCount={mediaCount}
            firstMedia={firstMedia}
          />
        </div>
      )}

      {/* Content */}
      <div className="px-5 lg:px-4 py-4">
        {/* Caption */}
        <CaptionWithReadMore caption={post.caption || ''} />

        {/* Channels - only show for scheduled/published posts, not MAPPED (no scheduling decision yet) */}
        {post.postChannels && post.postChannels.length > 0 && inferredStatus !== 'MAPPED' && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              {t('channels')} ({post.postChannels.length})
            </p>
            <div className="flex flex-wrap gap-2.5">
              {post.postChannels.map((pc) => {
                const status = pc.status;
                const externalUrl = pc.externalUrl;
                const errorMessage = pc.error;
                const profilePhotoUrl = pc.channel.profilePhotoUrl;

                // Build tooltip content
                const tooltipContent = (
                  <div className="text-xs">
                    <p className="font-medium">{pc.channel.platformUsername || pc.channel.platform}</p>
                    <p className="text-gray-400">{getPlatformDisplayName(pc.channel.platform)}</p>
                    {status && (
                      <p className={status === 'FAILED' ? 'text-red-400' : 'text-gray-400'}>
                        {getChannelStatusLabel(status, t)}
                      </p>
                    )}
                    {errorMessage && <p className="text-red-300 mt-1 max-w-xs break-words text-sm">{errorMessage}</p>}
                  </div>
                );

                return (
                  <Tooltip key={pc.id} content={tooltipContent} className="!z-[10001]">
                    <div className="relative">
                      {/* Avatar/Icon circle with status ring */}
                      <div
                        className={cn(
                          'w-8 h-8 rounded-full overflow-hidden flex items-center justify-center',
                          'ring-2 ring-offset-1',
                          status ? ringColors[status] || 'ring-gray-300' : 'ring-gray-300',
                          externalUrl && 'cursor-pointer hover:opacity-80 transition-opacity'
                        )}
                        onClick={(e) => {
                          if (externalUrl) {
                            e.stopPropagation();
                            window.open(externalUrl, '_blank', 'noopener,noreferrer');
                          }
                        }}
                      >
                        {profilePhotoUrl ? (
                          <img src={profilePhotoUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                            <FontAwesomeIcon
                              icon={platformIcons[pc.channel.platform] || faCloud}
                              className="text-gray-500 w-4 h-4"
                              aria-hidden="true"
                            />
                          </div>
                        )}
                      </div>

                      {/* Small platform badge overlay (bottom-right) */}
                      <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-white shadow-sm flex items-center justify-center">
                        <FontAwesomeIcon
                          icon={platformIcons[pc.channel.platform] || faCloud}
                          className="text-gray-600 text-[10px]"
                          aria-hidden="true"
                        />
                      </div>

                      {/* Failed indicator */}
                      {status === 'FAILED' && (
                        <div className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-red-500 border border-white" />
                      )}
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        )}
      </div>

      </div>{/* End scrollable content */}

      {/* Footer — fixed. Blog entries are read-only: navigate to the blog post instead of edit/delete */}
      {isBlog && (
        <div className="flex-shrink-0 px-5 lg:px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-end">
          <button
            data-close-sheet
            onClick={(e) => { e.stopPropagation(); navigate(`/blog/${post.blogPostId}`); }}
            className="flex min-h-[44px] items-center gap-1 rounded px-3 text-xs text-gray-500 transition-colors hover:bg-primary-50 hover:text-primary-600 lg:min-h-0 lg:px-2 lg:py-1"
            aria-label={t('view_details')}
          >
            <Icon name={ICONS.BLOG} size="xs" ariaHidden />
            {t('view_details')}
          </button>
        </div>
      )}

      {!isBlog && (canEdit || canDelete || canDrag) && (
        <div className="flex-shrink-0 px-5 lg:px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-center lg:justify-between gap-2">
          <span className="text-xs text-gray-400 hidden lg:inline">
            {canDrag ? t('drag_to_reschedule') : '\u00A0'}
          </span>
          {(canEdit || canDelete) && (
            <div className="flex items-center gap-1">
              {canEdit && (
                <button
                  data-close-sheet
                  onClick={(e) => { e.stopPropagation(); onEdit?.(post); }}
                  className="flex min-h-[44px] items-center gap-1 rounded px-3 text-xs text-gray-500 transition-colors hover:bg-primary-50 hover:text-primary-600 lg:min-h-0 lg:px-2 lg:py-1"
                  aria-label={t('edit')}
                >
                  <Icon name={ICONS.EDIT} size="xs" ariaHidden />
                  {t('edit')}
                </button>
              )}
              {canDelete && (
                <button
                  data-close-sheet
                  onClick={(e) => { e.stopPropagation(); onDelete?.(post); }}
                  className="flex min-h-[44px] items-center gap-1 rounded px-3 text-xs text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600 lg:min-h-0 lg:px-2 lg:py-1"
                  aria-label={t('remove_from_calendar')}
                >
                  <Icon name={ICONS.TRASH} size="xs" ariaHidden />
                  {t('remove')}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
